import React, { useContext, useState } from 'react'
import { MdLink } from 'react-icons/md'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Icon
} from '@clientbase/clientbase-library'
import { Box, Tooltip, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import { getChargeEnum } from 'templates/v2/Products/Page/Products.utils'

import Can from 'components/Can'
import CreditCardDetailsDialog from 'components/Modal/ModalRecurrenceDetail/components/CreditCardDetailsDialog'

import { formatMoney } from 'utils'
import { formatDate } from 'utils/FormatDate'
import { toastProps } from 'utils/types/toast-props'

import { theme } from 'styles/theme'

import { ModalChargeDetailContext } from '../../context'
import { cardTittle } from '../../ModalChargeDetail.utils'
import { S } from './'
import { getStatus } from './PaymentDetails.utils'

const CARD_PAYMENT_TYPES = [
  'credit_card',
  'credit_card_recurring',
  'credit_card_installments'
]

const PaymentDetails: React.FC = () => {
  const { paids, invoice, updateDialog, updateReload, updateDialogReverse } =
    useContext(ModalChargeDetailContext)
  const [isOpenCreditCardDialog, setIsOpenCreditCardDialog] = useState(false)

  const handleOpenCancelManuallyReceiptDialog = (isDirectPayment: boolean) => {
    if (!isDirectPayment) {
      handleOpenReversePayment()
      return
    }

    updateDialog({
      isOpenDialog: true,
      action: () => handleCancelManualReceipt(),
      description: 'Confirma cancelar o recebimento manual?',
      title: 'Cancelar recebimento manual',
      icon: 'info',
      actionLabel: 'Cancelar',
      cancelLabel: 'Voltar'
    })
  }

  const handleOpenReversePayment = () => {
    const uuid = getCardPaymentUuid()
    updateDialogReverse({ isOpen: true, uuid: uuid })
  }

  const handleCancelManualReceipt = async () => {
    const uuid = getPaymentUuid()

    const id = toast.loading('Cancelando o recebimento manual...')

    const response = await DataService({
      url: `/v1/payments/${uuid}/direct_payment`,
      type: 'DELETE'
    })

    response.status < 400 && updateReload((prevState) => !prevState)
    response.status < 400 && handleCloseCancelManuallyReceiptDialog()
    const errorMessage =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error ? errorMessage : response.data.message,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    // !!setReloadTable && setReloadTable((s) => !s)
  }

  const handleCloseCancelManuallyReceiptDialog = () => {
    updateDialog({
      isOpenDialog: false,
      action: () => ({}),
      description: '',
      title: '',
      icon: 'info',
      actionLabel: '',
      cancelLabel: ''
    })
  }

  const getPaymentUuid = () => {
    const uuid =
      invoice?.payments.find(
        (payment) =>
          payment.status === 'paid' &&
          payment.paymentType === 'direct_payment' &&
          payment
      )?.uuid || ''

    return uuid
  }

  const getCardPaymentUuid = () => {
    const uuid =
      invoice?.payments.find((payment) => payment.status === 'paid' && payment)
        ?.uuid || ''

    return uuid
  }

  if (paids?.length === 0 || paids === null || paids === undefined) {
    return null
  }

  return (
    <Accordion
      sx={{
        border: '1px solid #E8EBE8',
        borderLeft: '3px solid',
        borderLeftColor: 'primary.main',
        borderRadius: '8px !important',
        backgroundColor: '#FAFBFA',
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          margin: 0,
          backgroundColor: '#fff'
        }
      }}
    >
      <AccordionSummary
        aria-controls="paymentDetails-content"
        id="paymentDetails"
      >
        {cardTittle(
          'Detalhes do pagamento',
          'faCoins',
          `${theme.palette.primary.main}`
        )}
      </AccordionSummary>
      {!!paids && (
        <AccordionDetails>
          {paids.map((paid) => (
            <>
              <Box
                display="flex"
                gap={1.25}
                alignItems="end"
                margin={'10px 0px 10px 0px'}
              >
                <Typography variant="smLight" color="#777777">
                  Pagamento
                </Typography>
                <S.Separator />
                <Typography variant="smBold" display="flex" alignItems="center">
                  {formatDate(paid?.datePaid) || ''}
                </Typography>
              </Box>
              <Box
                display="flex"
                gap={1.25}
                alignItems="end"
                margin={'10px 0px 10px 0px'}
              >
                <Typography variant="smLight" color="#777777">
                  Valor pago
                </Typography>
                <S.Separator />
                <Typography variant="smBold" display="flex" alignItems="center">
                  {formatMoney(Number(paid?.amountPaid))}
                </Typography>
              </Box>
              <Box
                display="flex"
                gap={1.25}
                alignItems="end"
                margin={'10px 0px 10px 0px'}
              >
                <Typography variant="smLight" color="#777777">
                  Valor líquido
                </Typography>
                <S.Separator />
                <Typography variant="smBold" display="flex" alignItems="center">
                  {formatMoney(Number(paid?.amountPaid) - Number(paid?.fee))}
                </Typography>
              </Box>
              <Box
                display="flex"
                gap={1.25}
                alignItems="end"
                margin={'10px 0px 10px 0px'}
              >
                <Typography variant="smLight" color="#777777">
                  Taxas
                </Typography>
                <S.Separator />
                <Typography variant="smBold" display="flex" alignItems="center">
                  {formatMoney(Number(paid?.fee))}
                </Typography>
              </Box>
              {paid.receivables.length > 0 &&
                paid.receivables.map(({ expected_on, status }) => (
                  <>
                    {expected_on ? (
                      <Box
                        display="flex"
                        gap={1.25}
                        alignItems="end"
                        margin="10px 0px 10px 0px"
                      >
                        <Typography variant="smLight" color="#777777">
                          Data de liberação do saldo
                        </Typography>
                        <S.Separator sx={{ marginTop: '4px' }} />
                        <Typography
                          variant="smBold"
                          display="flex"
                          alignItems="center"
                        >
                          {formatDate(expected_on)}
                        </Typography>
                      </Box>
                    ) : null}

                    <Box
                      display="flex"
                      gap={1.25}
                      alignItems="end"
                      margin="10px 0px 10px 0px"
                    >
                      <Typography variant="smLight" color="#777777">
                        Status
                      </Typography>
                      <S.Separator sx={{ marginTop: '4px' }} />
                      <S.Status status={status}>
                        {getStatus(status).label}
                      </S.Status>
                    </Box>
                  </>
                ))}
              <Typography variant="smLight" color="#777777">
                Meio de pagamento utilizado
              </Typography>
              <Box
                display="flex"
                gap={1.25}
                alignItems="end"
                margin={'10px 0px 10px 0px'}
              >
                <Typography variant="smBold" display="flex" alignItems="center">
                  <Box alignItems="center" display="flex">
                    <Box
                      display="flex"
                      flexWrap={'wrap'}
                      alignItems={'center'}
                      gap={'8px'}
                    >
                      {paid?.paymentType === 'direct_payment' && (
                        <Box>
                          <Chip
                            variant="payment"
                            sx={{
                              bgcolor: theme.palette.neutral[400]
                            }}
                            key={'Recebimento manual'}
                            label={'RECEBIMENTO MANUAL'}
                          />
                        </Box>
                      )}

                      {paid?.paymentType === 'credit_card_installments' && (
                        <Box>
                          <Chip
                            variant="payment"
                            sx={{
                              bgcolor: theme.palette.neutral[400]
                            }}
                            key={`Cartão parcelado`}
                            label={
                              paid.paymentable?.installments &&
                              paid.paymentable?.installments > 1
                                ? `CARTÃO PARCELADO ${paid.paymentable?.installments}x`
                                : 'CARTÃO À VISTA'
                            }
                          />
                        </Box>
                      )}
                      {paid?.paymentType !== 'credit_card_installments' && (
                        <Chip
                          variant="payment"
                          sx={{
                            bgcolor: theme.palette.neutral[400]
                          }}
                          key={
                            paid?.paymentType !== 'direct_payment'
                              ? getChargeEnum(paid?.paymentType)
                              : getChargeEnum(paid?.paymentable?.directType)
                          }
                          label={
                            paid?.paymentType !== 'direct_payment'
                              ? getChargeEnum(paid?.paymentType).toUpperCase()
                              : getChargeEnum(
                                  paid?.paymentable?.directType
                                ).toUpperCase()
                          }
                        />
                      )}
                    </Box>

                    {CARD_PAYMENT_TYPES.includes(paid?.paymentType) &&
                      invoice?.chargedCreditCard && (
                        <Box ml="8px">
                          <Tooltip
                            enterTouchDelay={0}
                            arrow
                            placement="top"
                            title="Clique para ver detalhes do cartão"
                          >
                            <Box
                              onClick={() => setIsOpenCreditCardDialog(true)}
                              sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <Icon
                                icon="creditCardChecked"
                                sx={{
                                  color: theme.palette.neutral[200],
                                  '&:hover': {
                                    color: theme.palette.primary.main
                                  }
                                }}
                              />
                            </Box>
                          </Tooltip>
                        </Box>
                      )}

                    {invoice?.billingDocuments.length > 0 && (
                      <Box ml="8px">
                        <Tooltip
                          arrow={true}
                          placement="top"
                          title="Visualizar comprovante de pagamento"
                        >
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                          >
                            <a
                              href={
                                invoice?.billingDocuments[0].docFileUrl || ''
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <MdLink
                                style={{ marginRight: 8 }}
                                size={24}
                                color={theme.palette.neutral[200]}
                              />
                            </a>
                          </Box>
                        </Tooltip>
                      </Box>
                    )}

                    {paid?.paymentType !== 'pix' &&
                      paid?.paymentType !== 'boleto' && (
                        <Can permission="payments:refund">
                          <Button
                            variantButton="ghost"
                            sx={{ ml: '8px' }}
                            startIcon="delete"
                            onClick={() =>
                              handleOpenCancelManuallyReceiptDialog(
                                paid?.paymentType === 'direct_payment'
                              )
                            }
                          >
                            {paid?.paymentType !== 'direct_payment'
                              ? 'Estornar'
                              : 'Excluir'}
                          </Button>
                        </Can>
                      )}
                  </Box>
                </Typography>
              </Box>
            </>
          ))}
          {invoice?.status === 'paid' && invoice?.notes[0]?.comment && (
            <>
              <Typography variant="smLight" color="#777777">
                Anotações
              </Typography>
              <Box
                display="flex"
                gap={1.25}
                alignItems="end"
                margin={'10px 0px 10px 0px'}
              >
                <Typography variant="smBold" display="flex" alignItems="center">
                  {invoice?.notes[0]?.comment}
                </Typography>
              </Box>
            </>
          )}
        </AccordionDetails>
      )}
      <CreditCardDetailsDialog
        isOpen={isOpenCreditCardDialog}
        onClose={() => setIsOpenCreditCardDialog(false)}
        chargedCreditCard={invoice?.chargedCreditCard}
      />
    </Accordion>
  )
}

export default PaymentDetails
